import React, { useState, useEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Upload, FileText, X, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";

import toast from "react-hot-toast";
import apiClient from "../../utils/apiClient";
import moment from "moment-timezone";
import { Check, ChevronsUpDown, Edit2Icon, Trash2Icon } from "lucide-react";
import { cn } from "../../lib/utils";
import server from "../../utils/serverConfig";

const sideOptions = [
  { value: "1", label: "Buy" },
  { value: "-1", label: "Sell" },
];

const strategyOptions = [
  "PDI_MA_MBZ",
  "PDI_MDI_MBZ",
  "HHPC_MBZ",
  "HE_MBZ",
  "HE_PC5_MA10",
  "HE_PC2_MA10",
  "HHPC_PC2_MA10",
  "HHPC_PC5_MA10",
  "PDI_MDI_PC2_MA10",
  "PDI_MDI_PC5_MA10",
  "HE_PC5",
  "HE_PC2",
  "PDI_MA_PC2",
  "PDI_MA_PC5",
  "PDI_MDI_PC2",
  "PDI_MDI_PC5",
  "PDI_MA_PC2_MA10",
  "PDI_MA_PC5_MA10",
  "HHPC_PC2",
  "HHPC_PC5",
  "ORB_1",
];

export default function OrderTaggingSystem({ }) {
  const [userLoading, setUserLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const [strategyText, setStrategyText] = useState("");
  const [strategyDropdown, setStrategyDropdown] = useState("");
  const [side, setSide] = useState(""); // Buy/Sell
  const [selectedClient, setSelectedClient] = useState("");
  const [entry, setEntry] = useState(0);
  const [validity, setValidity] = useState(5);
  const [orderNumber, setOrderNumber] = useState("");
  const [openClient, setOpenClient] = useState(false);

  // CSV upload state
  const [csvRows, setCsvRows] = useState([]);           // parsed rows
  const [csvFileName, setCsvFileName] = useState("");
  const [csvResults, setCsvResults] = useState([]);     // per-row results after submit
  const [csvLoading, setCsvLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchFyersUserList = () => {
    setUserLoading(true);
    apiClient
      .get(`${server.godseye.baseUrl}get-fyers-users`)
      .then((response) => {
        const activeUsers = response.data.filter(
          (user) => user.status === "active"
        );

        setFilteredData(activeUsers);

        setUserLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching Fyers users:", error);
        setUserLoading(false);
      });
  };

  function getCandleTimestamp() {
    try {
      // Get current time in Asia/Kolkata
      const now = moment().tz("Asia/Kolkata");
      let hour = now.hour();
      let minute = now.minute();

      // Candle time logic
      if (minute <= 15) {
        hour -= 1;
      }
      minute = 15;

      // Create candle timestamp in IST
      const candleIST = moment.tz(
        {
          year: now.year(),
          month: now.month(), // month is 0-based in moment
          day: now.date(),
          hour,
          minute,
          second: 0,
        },
        "Asia/Kolkata"
      );

      // Convert to UTC
      const candleUTC = candleIST.utc();

      return candleUTC.toISOString();
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  useEffect(() => {
    fetchFyersUserList();
  }, []);

  const handleOrderTagging = () => {
    let finalStrategy = "";

    // Step 1: Prioritize strategy
    if (strategyText && strategyText.trim() !== "") {
      finalStrategy = strategyText.trim();
    } else if (strategyDropdown && strategyDropdown.trim() !== "") {
      finalStrategy = strategyDropdown.trim();
    } else {
      toast.error(
        "Please enter or select a strategy before placing the order."
      );
      return;
    }

    // Step 2: Prepare order payload
    const orderTaggingPayload = {
      ordernumber: orderNumber,
      strategy: finalStrategy,
      entry: parseInt(entry),
      side: parseInt(side),
      validity: parseInt(validity),
      clientid: selectedClient,
      candletimestamp: getCandleTimestamp(),
    };

    //   // Step 3: Console log the final data
    console.log("📝 Order Tagging data:", orderTaggingPayload);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}order-tagging`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(orderTaggingPayload), // Convert the data object to a JSON string
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        // Optionally handle the response data here
        toast.success("Order tagged successfully!");
      })
      .catch((error) => {
        console.error("Failed to add new user", error);
        toast.error("Failed to tag order. Please try again.");
      })
      .finally(() => {
        console.log("Order tagged completed");
      });


  };

  const handleResetForm = () => {
    setOrderNumber("");
    setEntry(0);
    setValidity(5);
    setSide("");
    setSelectedClient("");
    setStrategyDropdown("");
    setStrategyText("");
  };

  // ── CSV helpers ──────────────────────────────────────────────────────────────
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    setCsvResults([]);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      if (lines.length < 2) {
        toast.error("CSV must have a header row and at least one data row.");
        return;
      }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((line, idx) => {
        const vals = line.split(",").map((v) => v.trim());
        const row = {};
        headers.forEach((h, i) => { row[h] = vals[i] ?? ""; });
        row._rowIndex = idx;
        return row;
      });
      setCsvRows(rows);
    };
    reader.readAsText(file);
  };

  const handleClearCsv = () => {
    setCsvRows([]);
    setCsvFileName("");
    setCsvResults([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCsvSubmit = async () => {
    if (csvRows.length === 0) {
      toast.error("No CSV rows to submit.");
      return;
    }
    const ts = getCandleTimestamp();
    const payload = csvRows.map((row) => ({
      ordernumber: row.ordernumber,
      strategy: row.strategy,
      entry: parseInt(row.entry) || 0,
      side: parseInt(row.side) || 1,
      validity: parseInt(row.validity) || 5,
      clientid: row.clientid,
      candletimestamp: ts,
    }));

    setCsvLoading(true);
    try {
      const response = await apiClient.post(
        `${server.godseye.baseUrl}order-tagging`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      const { results } = response.data;
      setCsvResults(results || []);
      const ok = (results || []).filter((r) => r.status === "ok").length;
      toast.success(`Bulk tagging done: ${ok} / ${payload.length} inserted.`);
    } catch (err) {
      console.error(err);
      toast.error("Bulk submit failed. Check console.");
    } finally {
      setCsvLoading(false);
    }
  };

  const getCsvRowResult = (idx) =>
    csvResults.find((r) => String(r.ordernumber) === String(csvRows[idx]?.ordernumber));

  const statusBadge = (result) => {
    if (!result) return null;
    if (result.status === "ok")
      return <span className="flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" />OK</span>;
    if (result.status === "duplicate")
      return <span className="flex items-center gap-1 text-yellow-400 text-xs font-semibold"><AlertTriangle className="h-3.5 w-3.5" />Dup</span>;
    return <span className="flex items-center gap-1 text-red-400 text-xs font-semibold"><XCircle className="h-3.5 w-3.5" />Err</span>;
  };

  return (
    <div className="flex flex-col gap-4 w-full mx-auto px-[30px] py-[40px]">
      <div>
        <p className="flex items-center text-lg font-semibold text-white">
          Order Tagging System
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 py-4 max-w-[600px]">
        {/* Ordernumber Input */}
        <div className="grid grid-cols-1 gap-2">
          <Label className="text-white">Order Number</Label>
          <Input
            className="text-white"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="Enter order number"
          />
        </div>

        {/* Side Dropdown */}
        <div className="flex flex-col  gap-y-2">
          <Label className="text-white">Side</Label>
          <Select value={side} onValueChange={(val) => setSide(val)}>
            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
              <SelectValue placeholder="Buy / Sell" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700">
              <SelectGroup>
                {sideOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/*  Validity */}
        <div className="grid grid-cols-1 gap-2">
          <Label className="text-white">Validity</Label>
          <Input
            type="number"
            className="text-white"
            value={validity}
            placeholder="Enter validity value"
            onChange={(e) => setValidity(Number(e.target.value))}
          />
        </div>

        {/* Entry */}
        <div className="grid grid-cols-1 gap-2">
          <Label className="text-white">Entry</Label>
          <Input
            type="number"
            className="text-white"
            value={entry}
            placeholder="Enter entry value"
            onChange={(e) => setEntry(Number(e.target.value))}
          />
        </div>

        {/* Clients Dropdown */}
        <div className="flex flex-col gap-y-2">
          <Label className="text-white">Select Client</Label>
          <Popover open={openClient} onOpenChange={setOpenClient}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openClient}
                className=" justify-between text-white text-[15px]"
              >
                {selectedClient
                  ? filteredData.find(
                    (client) => client.client_id === selectedClient
                  )?.client_id +
                  " (" +
                  filteredData.find(
                    (client) => client.client_id === selectedClient
                  )?.name +
                  ")"
                  : "Select Client"}
                <ChevronsUpDown className="opacity-80 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className=" p-0 border-zinc-800">
              <Command>
                <CommandInput
                  placeholder="Search client..."
                  className="font-poppins"
                />
                <CommandList>
                  <CommandEmpty>No client found.</CommandEmpty>
                  <CommandGroup>
                    {userLoading ? (
                      <div className="flex items-center justify-center p-2">
                        loading clients ...
                      </div>
                    ) : (
                      filteredData.map((client) => (
                        <CommandItem
                          key={client.client_id}
                          value={client.client_id}
                          className="font-poppins"
                          onSelect={(currentValue) => {
                            setSelectedClient(
                              currentValue === selectedClient
                                ? ""
                                : currentValue
                            );
                            setOpenClient(false);
                          }}
                        >
                          {client.client_id} ({client.name})
                          <Check
                            className={cn(
                              "ml-auto h-5 w-5",
                              selectedClient === client.client_id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Strategy Text Input */}
        <div className="flex items-center gap-x-6 w-full">
          <div className="w-full grid grid-cols-1 gap-2">
            <Label className="text-white">Strategy Text</Label>
            <Input
              className="text-white"
              value={strategyText}
              onChange={(e) => setStrategyText(e.target.value)}
              placeholder="Enter strategy manually"
            />
          </div>

          {/* Strategy Dropdown */}
          <div className="w-full flex flex-col gap-y-2">
            <Label className="text-white">Strategy Dropdown</Label>
            <Select
              value={strategyDropdown}
              onValueChange={(val) => setStrategyDropdown(val)}
            >
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Strategy" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700 h-[190px] overflow-y-auto">
                <SelectGroup>
                  {strategyOptions.map((strategy) => (
                    <SelectItem key={strategy} value={strategy}>
                      {strategy}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-6 items-center py-4 w-full max-w-[600px]">
        <Button
          className="w-[180px] text-[16px] bg-zinc-900 text-white font-poppins font-semibold hover:bg-zinc-800"
          onClick={handleResetForm}
        >
          Reset
        </Button>
        <Button
          className="w-[180px] text-[16px] bg-[#07F2C7]/80 text-black font-poppins font-semibold hover:bg-[#07F2C7] hover:text-black"
          onClick={handleOrderTagging}
        >
          Add Order Tag
        </Button>
      </div>

      {/* ── CSV Bulk Upload Section ─────────────────────────────────────────── */}
      <div className="mt-6 border border-zinc-700 rounded-xl p-6 max-w-[900px] flex flex-col gap-4">
        <p className="text-white font-semibold text-base flex items-center gap-2">
          <Upload className="h-4 w-4 text-[#07F2C7]" />
          Bulk Tag via CSV
        </p>
        <p className="text-zinc-400 text-xs">
          CSV columns (header row required):{" "}
          <code className="text-[#07F2C7]">
            ordernumber, strategy, entry, side, validity, clientid
          </code>
          <br />
          <span className="text-zinc-500">
            <code>candletimestamp</code> is auto-generated on submit.
          </span>
        </p>

        {/* File picker */}
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            id="csv-upload"
            className="hidden"
            onChange={handleCsvFileChange}
          />
          <Button
            className="bg-zinc-800 text-white hover:bg-zinc-700 text-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileText className="h-4 w-4 mr-2" />
            Choose CSV
          </Button>
          {csvFileName && (
            <span className="text-zinc-300 text-sm flex items-center gap-2">
              {csvFileName}
              <button onClick={handleClearCsv} className="text-zinc-500 hover:text-red-400">
                <X className="h-4 w-4" />
              </button>
            </span>
          )}
        </div>

        {/* Preview table */}
        {csvRows.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-zinc-700">
              <table className="w-full text-xs text-left">
                <thead className="bg-zinc-800 text-zinc-400">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Order No.</th>
                    <th className="px-3 py-2">Strategy</th>
                    <th className="px-3 py-2">Entry</th>
                    <th className="px-3 py-2">Side</th>
                    <th className="px-3 py-2">Validity</th>
                    <th className="px-3 py-2">Client</th>
                    {csvResults.length > 0 && <th className="px-3 py-2">Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {csvRows.map((row, idx) => {
                    const result = getCsvRowResult(idx);
                    return (
                      <tr
                        key={idx}
                        className={`border-t border-zinc-700 ${result?.status === "ok"
                            ? "bg-emerald-950/30"
                            : result?.status === "duplicate"
                              ? "bg-yellow-950/30"
                              : result?.status === "error"
                                ? "bg-red-950/30"
                                : idx % 2 === 0
                                  ? "bg-zinc-900"
                                  : "bg-zinc-800/60"
                          }`}
                      >
                        <td className="px-3 py-2 text-zinc-500">{idx + 1}</td>
                        <td className="px-3 py-2 text-white font-mono">{row.ordernumber}</td>
                        <td className="px-3 py-2 text-zinc-300">{row.strategy}</td>
                        <td className="px-3 py-2 text-zinc-300">{row.entry}</td>
                        <td className="px-3 py-2 text-zinc-300">{row.side === "1" ? "Buy" : "Sell"}</td>
                        <td className="px-3 py-2 text-zinc-300">{row.validity}</td>
                        <td className="px-3 py-2 text-zinc-300">{row.clientid}</td>
                        {csvResults.length > 0 && (
                          <td className="px-3 py-2">{statusBadge(result)}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-4 mt-2">
              <span className="text-zinc-400 text-xs">{csvRows.length} row(s) ready</span>
              <Button
                className="bg-[#07F2C7]/80 text-black font-poppins font-semibold hover:bg-[#07F2C7] text-sm px-6"
                onClick={handleCsvSubmit}
                disabled={csvLoading}
              >
                {csvLoading ? "Submitting..." : "Submit Bulk Tags"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
